import {
  sendMessage,
  receiveMessage,
  setError,
  setWaiting,
  cancelRequest,
  resetMessages,
  setLastRequestData,
} from './slice'

import { createAsyncThunk } from '@reduxjs/toolkit'

import { RootState } from '@app/model/store'

import { API_PUBLIC } from '@shared/api/api.public'
import { TypeMappingRestOutput } from '@shared/api/middleware/mappingAPI/restaurant/type'
import { Assistant } from '@shared/api/types/swaggerTypes'
import { assistant_parse_answer } from '@shared/api/middleware/mappingAPI'
import { mappingRestaurantItem } from '@shared/api/middleware/mappingAPI/restaurant/restaurant'

// глобальный AbortController для отмены текущего запроса
let abortController: AbortController | null = null

/**
 * @function formatQuestion
 * @returns {string} - Форматированный текст сообщения
 */
function formatQuestion(text: string, cityName: string, citySlug: string, cityId: string, template: string) {
  // Убираем переносы строк и возвраты каретки
  const userMsg = text.replace(/[\n\r]/g, ' ')

  // заменяем плейсхолдеры в шаблоне
  const stringTemplate = template
    .replace(/{userMsg}/g, userMsg)
    .replace(/{cityName}/g, cityName)
    .replace(/{citySlug}/g, citySlug)
    .replace(/{cityId}/g, cityId)

  return stringTemplate
}

/**
 * @function sendMessageRequest
 * @param {string} text - Текст сообщения
 * @param {Function} dispatch - отправка сообщения
 * @returns {Promise<void>}
 */
export const sendMessageRequest = createAsyncThunk(
  'gigAssist/sendMessage',
  async (text: string, { dispatch, getState }) => {
    try {
      dispatch(sendMessage(text))
      dispatch(setWaiting())

      // отменяем предыдущий запрос, если он есть
      if (abortController) {
        abortController.abort()
      }

      abortController = new AbortController()

      const state = getState() as RootState
      const uuid = state.gigAssist?.uuid
      const city = state.global.currentCity
      const messageTemplate = state.gigAssist?.messageTemplate || '{userMsg}'
      const lastRequestData = state.gigAssist.lastRequest

      if (lastRequestData.status === 'error' && lastRequestData.requestText === text) {
        throw new Error(lastRequestData.responseText)
      }

      if (!city) {
        throw new Error('данные города не найдены')
      }

      // Отправляем сообщение ассистенту
      const response: Assistant = await API_PUBLIC.assistant_create({
        body: {
          question: formatQuestion(text, city.name, city.slug, city.id.toString(), messageTemplate),
        },
        config: { signal: abortController.signal },
      })

      // console.log('!!! sendMessageRequest response', response)

      // Получаем историю чата
      const chatHistory = await API_PUBLIC.assistant_list({
        params: {
          uuid: response.uuid || uuid,
          page: 1,
          page_size: 10,
        },
        config: { signal: abortController.signal },
      })

      // console.log('!!! sendMessageRequest chatHistory', chatHistory)

      // Находим последний ответ ассистента
      const lastAssistantMessage: Assistant | undefined = chatHistory.results?.find((message: Assistant) => {
        return message.answer && message.uuid === (response.uuid || uuid)
      })

      // Парсим ответ ассистента на массив ресторанов
      const parsedResult = assistant_parse_answer(lastAssistantMessage?.answer)
      // console.log('!!! sendMessageRequest parsedResult', parsedResult)

      const parsedRests = parsedResult.restaurants
      const validRests = parsedRests.filter((r) => r.id)

      // для каждого ресторана делаем запрос карточки и добавляем conclusion
      const getRestaurantCard = async (rest): Promise<TypeMappingRestOutput | null> => {
        try {
          // фильтруем невалидные id
          if (!rest || typeof rest.id !== 'number' || !rest.id) return null

          const restList = await API_PUBLIC.restaurant_list({
            params: { id: rest.id, page: 1, page_size: 1 },
            config: { signal: abortController.signal },
          })

          const restData = restList?.results?.[0]

          // добавляем conclusion как отдельное поле
          if (restData && rest.conclusion) {
            const resultWithConclusion = {
              ...restData,
              conclusion: rest.conclusion,
            }
            // console.log('!!! sendMessageRequest resultWithConclusion', resultWithConclusion)
            return resultWithConclusion
          }
          // console.log('!!! sendMessageRequest returning restData without conclusion', restData)
          return restData
        } catch (e) {
          console.error('!!! sendMessageRequest getRestaurantCard error', e)
          return null
        }
      }

      // получаем карточки ресторанов
      const responseRests: TypeMappingRestOutput[] = await Promise.all(validRests.map(getRestaurantCard)).then(
        (results) => {
          const filteredResults = results.filter((x): x is TypeMappingRestOutput => Boolean(x))
          // console.log('!!! sendMessageRequest filtered restaurants', filteredResults)
          return filteredResults
        },
      )

      // console.log('!!! sendMessageRequest responseRests', {
      //   responseRests,
      //   length: responseRests?.length,
      //   type: typeof responseRests,
      // })

      // * формируем текст сообщения *
      // если есть карточки ресторанов, показываем только основное сообщение
      // чтобы избежать дублирования информации
      let messageText = parsedResult.message || lastAssistantMessage?.answer || 'Кажется, что-то сломалось'
      // если есть карточки ресторанов, то не показываем текстовое описание ресторанов в сообщении
      if (responseRests && responseRests.length > 0) {
        // используем только основное сообщение, без описания ресторанов
        messageText = parsedResult.message || 'Вот несколько ресторанов, которые могут вас заинтересовать:'
      }

      const messagePayload = {
        text: messageText,
        restaurants: responseRests || [],
        uuid: response.uuid || uuid,
      }

      // console.log('!!! sendMessageRequest messagePayload', messagePayload)

      dispatch(receiveMessage(messagePayload))
      dispatch(
        setLastRequestData({
          requestText: text,
          responseText: messagePayload.text,
          status: 'success',
        }),
      )

      if (!lastAssistantMessage?.answer) {
        throw new Error('Не удалось получить ответ от ассистента')
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        // отмена запроса — не показываем ошибку
        dispatch(cancelRequest())
      } else {
        dispatch(setError(error.message))
      }

      dispatch(
        setLastRequestData({
          requestText: text,
          responseText: error.message,
          status: 'error',
        }),
      )
    } finally {
      abortController = null
    }
  },
)

/**
 * @function cancelMessageRequest
 * @returns {void}
 */
export const cancelMessageRequest = () => (dispatch) => {
  if (abortController) {
    abortController.abort()
    abortController = null
  }
  dispatch(cancelRequest())
}
